import gsap from 'gsap'
import SplitText from 'gsap/dist/SplitText'

import {
  PAGE_TRANSITION_ENTER_DELAY,
  PAGE_TRANSITION_LEAVE_DURATION
} from '@/contexts/transition'
import { useRouterStore } from '@/store/router'
import { MediaQuery } from '@/constants'

import {
  getScrollTriggerHorizontalOptions,
  getScrollTriggerVerticalOptions,
  isElementInViewport
} from './utils'
import { Animation } from '@/types'

export const nextSectionRevealAnimation = ({
  node,
  reverse,
  context
}: Animation) => {
  const selector = context?.selector || gsap.utils.selector(node)

  const title = selector('[data-motion-title]')[0]
  const overline = selector('[data-motion-overline]')[0]
  const btnWrapper = selector('[data-motion-btn-wrapper]')
  const btnContent = selector('[data-motion-btn-content]')

  const titleSplitTextWrap = new SplitText(title, {
    type: 'chars',
    charsClass: 'char-wrap'
  })
  const titleSplitText = new SplitText(title, {
    type: 'chars',
    charsClass: 'char'
  })

  const overlineSplitTextWrap = new SplitText(overline, {
    type: 'chars',
    charsClass: 'char-wrap'
  })
  const overlineSplitText = new SplitText(overline, {
    type: 'chars',
    charsClass: 'char'
  })

  const matchMedia = gsap.matchMedia(node)

  matchMedia.add(MediaQuery.SMALL, () => {
    const inView = isElementInViewport(node, false)

    gsap
      .timeline({
        ...getScrollTriggerVerticalOptions(node, reverse),
        delay: inView ? PAGE_TRANSITION_ENTER_DELAY : 0
      })
      .add(
        gsap
          .timeline()
          .fromTo(
            overlineSplitText.chars,
            { yPercent: 110 },
            {
              yPercent: 0,
              duration: 0.8,
              ease: 'power2.out',
              stagger: { amount: 0.3 }
            }
          )
          .fromTo(
            titleSplitText.chars,
            { yPercent: 110 },
            {
              yPercent: 0,
              duration: 0.8,
              ease: 'power2.out',
              stagger: { amount: 0.3 }
            },
            '<+0.12'
          )
      )
      .add(
        gsap
          .timeline()
          .fromTo(
            btnWrapper,
            { scale: 0 },
            {
              scale: 1,
              duration: 1,
              stagger: 0.12,
              ease: 'power3.out'
            }
          )
          .fromTo(
            btnContent,
            { scale: 2 },
            {
              scale: 1,
              duration: 1,
              stagger: 0.12,
              ease: 'power3.out'
            },
            '<'
          ),
        '<+0.36'
      )
  })

  matchMedia.add(MediaQuery.LARGE, () => {
    const inView = isElementInViewport(node, true)

    gsap
      .timeline({
        ...getScrollTriggerHorizontalOptions(node, reverse),
        delay: inView ? PAGE_TRANSITION_ENTER_DELAY : 0
      })
      .add(
        gsap
          .timeline()
          .fromTo(
            overlineSplitText.chars,
            { yPercent: 110 },
            {
              yPercent: 0,
              duration: 0.8,
              ease: 'power2.out',
              stagger: { amount: 0.3 }
            }
          )
          .fromTo(
            titleSplitText.chars,
            { yPercent: 110 },
            {
              yPercent: 0,
              duration: 0.8,
              ease: 'power2.out',
              stagger: { amount: 0.3 }
            },
            '<+0.12'
          )
      )
      .add(
        gsap
          .timeline()
          .fromTo(
            btnWrapper,
            { scale: 0 },
            {
              scale: 1,
              duration: 1,
              stagger: 0.12,
              ease: 'power3.out'
            }
          )
          .fromTo(
            btnContent,
            { scale: 2 },
            {
              scale: 1,
              duration: 1,
              stagger: 0.12,
              ease: 'power3.out'
            },
            '<'
          ),
        '<+0.36'
      )
  })

  return () => {
    const router = useRouterStore.getState()
    const needsRevertDelay =
      router.previousRoute !== undefined &&
      router.currentRoute !== router.previousRoute

    const revert = () => {
      if (titleSplitTextWrap) {
        titleSplitTextWrap.revert()
      }

      if (overlineSplitTextWrap) {
        overlineSplitTextWrap.revert()
      }

      matchMedia.revert()
    }

    needsRevertDelay
      ? gsap.delayedCall(PAGE_TRANSITION_LEAVE_DURATION, revert)
      : revert()
  }
}
